/**
 * Create Work Form
 * Standalone work creation. Created works start unsanctioned (no admn_snctn_id)
 * so they show up in the Pending tab ready to be picked into an admin sanction.
 *
 * Optionally, an existing "orphan" estimate (wrk_id IS NULL) can be picked — the
 * form prefills from it and links the estimate to the newly-created work on save.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { createPortal } from 'react-dom';
import axiosInstance from '@/services/interceptor';
import { worksApi, estimatesApi, mastersApi } from '../../services/wmsService';
import { PROCUREMENT_TYPE_OPTIONS, type ProcurementType } from '../../lib/procurementType';
import { useToast } from '@/hooks/useToast';
import { useWorkMasterList } from '../../hooks/useWorkMasterList';
import { HelpButton } from '../../help';
import { PUSHKARALU_TAG } from '../../lib/pushkaralu';
import { UdaRequiredField } from '../common/UdaRequiredField';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

interface CreateWorkFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (createdWork: any) => void;
}

interface OrphanEstimate {
  estmt_id: number;
  estmt_cd: string;
  estmt_nm: string;
  estmt_type_cd?: string;
  totl_at?: number;
  sts_cd?: string;
  // Optional dimensions carried on the estimate (added in migration 010)
  admn_unt_id?: number | null;
  admn_unt_nm?: string | null;
  wrk_type_id?: number | null;
  wrk_type_nm?: string | null;
  wrk_ctgry_id?: number | null;
  wrk_ctgry_nm?: string | null;
  schme_id?: number | null;
  schme_nm?: string | null;
}

export default function CreateWorkForm({ isOpen, onClose, onSuccess }: CreateWorkFormProps) {
  const { success: showSuccess, error: showError } = useToast();
  const { workTypes, workCategories, schemes, financialYears, eventTags, pushkaraluActivities } = useWorkMasterList();

  const [saving, setSaving] = useState(false);

  // Form fields
  const [wrk_nm, setWrkNm] = useState('');
  const [wrk_type_id, setWrkTypeId] = useState<number | null>(null);
  const [wrk_ctgry_id, setWrkCtgryId] = useState<number | null>(null);
  // WORKS / GOODS / SERVICES (server migration 217). Only GOODS lines become assets.
  const [prcrmnt_type_cd, setPrcrmntTypeCd] = useState<ProcurementType>('WORKS');
  const [schme_id, setSchmeId] = useState<number | null>(null);
  const [fy_id, setFyId] = useState<number | null>(null);
  const [admn_unt_id, setAdmnUntId] = useState<number | null>(null);
  // The UDA the work belongs to. Mandatory (the server refuses a work without one).
  const [uda_id, setUdaId] = useState<number | null>(null);
  const [udaError, setUdaError] = useState<string | undefined>();
  const [lctn_tx, setLctnTx] = useState('');
  const [ward_nm, setWardNm] = useState('');
  const [orgnl_estmt_at, setOrgnlEstmtAt] = useState<number>(0);
  const [prty_nu, setPrtyNu] = useState<number | ''>('');
  const [rmrks_tx, setRmrksTx] = useState('');

  // Event Tag & Pushkaralu Activity — both driven by backend masters (by id).
  // eventTagId '' == normal work. When the selected tag's code is PUSHKARALU,
  // the work is tagged, the UDA list narrows to the 6 host UDAs, and an Activity
  // dropdown appears.
  const [eventTagId, setEventTagId] = useState('');
  const [pushkaraluActvtyId, setPushkaraluActvtyId] = useState('');
  const selectedEventTag = eventTags.find((t: any) => String(t.evnt_tag_id) === eventTagId);
  const isPushkaralu = selectedEventTag?.evnt_tag_cd === PUSHKARALU_TAG;
  const selectedActivity = pushkaraluActivities.find((a: any) => String(a.pushkaralu_actvty_id) === pushkaraluActvtyId);

  // UDAs (Admin Units) — full list, plus the DB-flagged Pushkaralu host UDAs.
  const [ulbs, setUdas] = useState<Array<{ admn_unt_id: number; admn_unt_nm: string }>>([]);
  const [pushkaraluUlbs, setPushkaraluUlbs] = useState<Array<{ admn_unt_id: number; admn_unt_nm: string }>>([]);

  // Orphan estimates (server-side search)
  const [orphanEstimates, setOrphanEstimates] = useState<OrphanEstimate[]>([]);
  const [orphanTotal, setOrphanTotal] = useState(0);
  const [selectedEstmtId, setSelectedEstmtId] = useState<number | null>(null);
  const [estmtSearch, setEstmtSearch] = useState('');
  const [estmtLoading, setEstmtLoading] = useState(false);
  const estmtSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetForm = () => {
    setWrkNm(''); setWrkTypeId(null); setWrkCtgryId(null); setSchmeId(null);
    setFyId(null); setAdmnUntId(null); setUdaId(null); setUdaError(undefined); setLctnTx(''); setWardNm('');
    setOrgnlEstmtAt(0); setPrtyNu(''); setRmrksTx('');
    setSelectedEstmtId(null); setEstmtSearch('');
    setEventTagId(''); setPushkaraluActvtyId('');
  };

  // UDA options shown in the dropdown — restricted to the DB-flagged host UDAs
  // (adm_ulb_lst_t.is_pushkaralu_in) when the Pushkaralu tag is selected,
  // otherwise the full active-UDA list.
  const ulbOptions = isPushkaralu ? pushkaraluUlbs : ulbs;

  // Load UDAs (full list + Pushkaralu host list)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const [all, pk] = await Promise.all([
          mastersApi.getAdminUnits(true, 'UDA'),
          mastersApi.getPushkaraluUlbs(),
        ]);
        setUdas((all || []) as any);
        setPushkaraluUlbs((pk || []) as any);
      } catch (e) {
        console.error('Error loading UDAs:', e);
      }
    })();
  }, [isOpen]);

  // Server-side search: debounced call. Returns first 50 matches for the query.
  // Works across all orphan estimates, not a fixed top-N slice.
  const loadOrphanEstimates = useCallback(async (search: string) => {
    setEstmtLoading(true);
    try {
      const result = await estimatesApi.getAll({ noWork: true, search } as any, 1, 50);
      setOrphanEstimates((result.data || []) as any);
      setOrphanTotal(result.pagination?.total ?? (result.data || []).length);
    } catch (e) {
      console.error('Error loading orphan estimates:', e);
      setOrphanEstimates([]);
      setOrphanTotal(0);
    } finally {
      setEstmtLoading(false);
    }
  }, []);

  // Open/close: reset + initial load
  useEffect(() => {
    if (!isOpen) { resetForm(); return; }
    loadOrphanEstimates('');
  }, [isOpen, loadOrphanEstimates]);

  // Debounce server-side search on every keystroke
  useEffect(() => {
    if (!isOpen) return;
    if (estmtSearchTimeoutRef.current) clearTimeout(estmtSearchTimeoutRef.current);
    estmtSearchTimeoutRef.current = setTimeout(() => {
      loadOrphanEstimates(estmtSearch.trim());
    }, 300);
    return () => { if (estmtSearchTimeoutRef.current) clearTimeout(estmtSearchTimeoutRef.current); };
  }, [estmtSearch, isOpen, loadOrphanEstimates]);

  // When user picks an orphan estimate, prefill the work fields from it.
  // Only fill empty fields so an edit-in-progress is not clobbered.
  useEffect(() => {
    if (!selectedEstmtId) return;
    const est = orphanEstimates.find((e) => e.estmt_id === selectedEstmtId);
    if (!est) return;
    if (!wrk_nm) setWrkNm(est.estmt_nm || '');
    if (!orgnl_estmt_at) setOrgnlEstmtAt(Number(est.totl_at || 0));
    if (!wrk_type_id && est.wrk_type_id) setWrkTypeId(est.wrk_type_id);
    if (!wrk_ctgry_id && est.wrk_ctgry_id) setWrkCtgryId(est.wrk_ctgry_id);
    if (!schme_id && est.schme_id) setSchmeId(est.schme_id);
    if (!admn_unt_id && est.admn_unt_id) setAdmnUntId(est.admn_unt_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstmtId]);

  const handleSave = async () => {
    if (!uda_id) { setUdaError('Select a UDA'); showError('Please select a UDA'); return; }
    if (!wrk_nm.trim()) { showError('Work Name is required'); return; }
    setSaving(true);
    try {
      const created = await worksApi.create({
        wrk_nm: wrk_nm.trim(),
        uda_id,
        wrk_type_id, wrk_ctgry_id, schme_id, fy_id, admn_unt_id,
        prcrmnt_type_cd,
        lctn_tx: lctn_tx.trim() || null, ward_nm: ward_nm.trim() || null,
        orgnl_estmt_at: Number(orgnl_estmt_at) || 0,
        snctn_at: 0,
        prty_nu: prty_nu === '' ? null : Number(prty_nu),
        rmrks_tx: rmrks_tx.trim() || null,
        evnt_tag_id: eventTagId ? Number(eventTagId) : null,
        pushkaralu_actvty_id: isPushkaralu && pushkaraluActvtyId ? Number(pushkaraluActvtyId) : null,
        is_pushkaralu_in: isPushkaralu,
        pushkaralu_actvty_cd: isPushkaralu ? (selectedActivity?.pushkaralu_actvty_cd || null) : null
      } as any);

      // Link orphan estimate to the new work (if one was picked)
      if (selectedEstmtId && created?.workId) {
        try {
          await axiosInstance.put(`ds-wms/estimates/${selectedEstmtId}`, { wrk_id: created.workId });
        } catch (linkErr) {
          console.error('Work created but failed to link estimate:', linkErr);
          showError('Work created, but linking the estimate failed. You can link it manually.');
        }
      }

      showSuccess(`Work ${created?.workCode || ''} created — find it in the Pending tab.`);
      onSuccess(created);
      onClose();
    } catch (e: any) {
      console.error('Error creating work:', e);
      showError(e.response?.data?.message || 'Failed to create work');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500';

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto z-[9999]">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white w-full max-w-3xl rounded-xl shadow-xl">
          {/* Header */}
          <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={WMS_POPUP_ICON}>
                <i className="ri-building-2-line"></i>
              </div>
              <div className="min-w-0">
                <h2 className={WMS_POPUP_TITLE}>Create Work</h2>
                <p className={WMS_POPUP_SUBTITLE}>New work enters the Pending tab, ready for admin sanction.</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <HelpButton topic="wms.works.create-in-as" anchor="from-estimate" title="How to create a work — from an estimate" />
              <button onClick={onClose} className={WMS_POPUP_CLOSE}>
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 max-h-[75vh] overflow-y-auto space-y-5 bg-gray-50">
            {/* Orphan Estimate Picker */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                <i className="ri-file-list-3-line text-purple-600"></i>
                Start from an existing estimate (optional)
              </h3>
              <p className="text-[11px] text-gray-500 mb-2 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Only estimates that do not have a work associated are shown here.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type="text" value={estmtSearch} onChange={(e) => setEstmtSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Search by estimate code or name..." />
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {orphanEstimates.length}
                  {orphanTotal > orphanEstimates.length && <> of {orphanTotal.toLocaleString()}</>}
                  {orphanTotal > orphanEstimates.length && <span className="ml-1 text-gray-400">· refine search to narrow</span>}
                </span>
              </div>
              {estmtLoading ? (
                <ListSkeleton rows={3} avatar={false} />
              ) : orphanEstimates.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-xs italic">
                  {estmtSearch.trim()
                    ? `No estimates match "${estmtSearch.trim()}" among those without a work.`
                    : 'No estimates are available for which work is not created.'}
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {orphanEstimates.map((e) => {
                    const picked = selectedEstmtId === e.estmt_id;
                    return (
                      <label key={e.estmt_id}
                        className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer text-sm ${picked ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <input type="radio" name="orphan-estmt" checked={picked}
                            onChange={() => setSelectedEstmtId(picked ? null : e.estmt_id)}
                            className="accent-purple-600" />
                          <span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{e.estmt_cd}</span>
                          <span className="truncate text-gray-800">{e.estmt_nm}</span>
                        </div>
                        <span className="text-xs text-gray-600 font-mono">₹{Number(e.totl_at || 0).toLocaleString('en-IN')}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {selectedEstmtId && (
                <div className="mt-2 flex items-center gap-2 text-xs text-purple-700">
                  <i className="ri-link"></i> The selected estimate will be linked to this new work on save.
                  <button type="button" onClick={() => setSelectedEstmtId(null)} className="ml-auto text-gray-500 hover:text-gray-700 underline">Clear</button>
                </div>
              )}
            </div>

            {/* Work Details */}
            <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <i className="ri-edit-box-line text-purple-600"></i> Work Details
              </h3>
              <UdaRequiredField
                value={uda_id}
                onChange={(v) => { setUdaId(v); setUdaError(undefined); }}
                error={udaError}
                hint="The work belongs to this UDA."
              />
              <div>
                <label className={labelCls}>What are you procuring? <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {PROCUREMENT_TYPE_OPTIONS.map((opt) => {
                    const selected = prcrmnt_type_cd === opt.code;
                    return (
                      <button key={opt.code} type="button" onClick={() => setPrcrmntTypeCd(opt.code)} title={opt.hint}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm text-left ${selected ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        <i className={`${opt.icon} text-lg`}></i>
                        <span>
                          <span className="block font-medium">{opt.label}</span>
                          <span className="block text-[11px] text-gray-500">{opt.hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={labelCls}>Work Name <span className="text-red-500">*</span></label>
                <textarea value={wrk_nm} onChange={(e) => setWrkNm(e.target.value)} rows={2} className={inputCls}
                  placeholder="Enter work name" />
              </div>

              {/* Event Tag — routes this work into an event dashboard. Selecting
                  Pushkaralu tags the work, narrows the UDA list to the 6 host
                  UDAs, and reveals the Activity dropdown below. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Event Tag</label>
                  <select value={eventTagId} onChange={(e) => { setEventTagId(e.target.value); setPushkaraluActvtyId(''); setAdmnUntId(null); }} className={inputCls}>
                    <option value="">None (normal work)</option>
                    {eventTags.map((t: any) => <option key={t.evnt_tag_id} value={t.evnt_tag_id}>{t.evnt_tag_nm}</option>)}
                  </select>
                </div>
                {isPushkaralu && (
                  <div>
                    <label className={labelCls}>Pushkaralu Activity</label>
                    <select value={pushkaraluActvtyId} onChange={(e) => setPushkaraluActvtyId(e.target.value)} className={inputCls}>
                      <option value="">Select Activity</option>
                      {pushkaraluActivities.map((a: any) => <option key={a.pushkaralu_actvty_id} value={a.pushkaralu_actvty_id}>{a.pushkaralu_actvty_nm}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {isPushkaralu && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  Tagged as a Pushkaralu work — it will appear only in the Pushkaralu dashboard, not in the regular Works list or dashboards.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Work Type/Sector</label>
                  <select value={wrk_type_id || ''} onChange={(e) => setWrkTypeId(parseInt(e.target.value) || null)} className={inputCls}>
                    <option value="">Select Type</option>
                    {workTypes.map((t: any) => <option key={t.wrk_type_id} value={t.wrk_type_id}>{t.wrk_type_nm}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Work Category</label>
                  <select value={wrk_ctgry_id || ''} onChange={(e) => setWrkCtgryId(parseInt(e.target.value) || null)} className={inputCls}>
                    <option value="">Select Category</option>
                    {workCategories.map((c: any) => <option key={c.wrk_ctgry_id} value={c.wrk_ctgry_id}>{c.wrk_ctgry_nm}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Scheme</label>
                  <select value={schme_id || ''} onChange={(e) => setSchmeId(parseInt(e.target.value) || null)} className={inputCls}>
                    <option value="">Select Scheme</option>
                    {schemes.map((s: any) => <option key={s.schme_id} value={s.schme_id}>{s.schme_nm}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Financial Year</label>
                  <select value={fy_id || ''} onChange={(e) => setFyId(parseInt(e.target.value) || null)} className={inputCls}>
                    <option value="">Select FY</option>
                    {financialYears.map((fy: any) => <option key={fy.fy_id} value={fy.fy_id}>{fy.fy_nm}</option>)}
                  </select>
                </div>
                {/* Host location for a Pushkaralu work only. Every work's UDA is chosen in the
                    mandatory UDA field above. */}
                {isPushkaralu && (
                  <div>
                    <label className={labelCls}>Pushkaralu Host</label>
                    <select value={admn_unt_id || ''} onChange={(e) => setAdmnUntId(parseInt(e.target.value) || null)} className={inputCls}>
                      <option value="">Select host</option>
                      {ulbOptions.map((u: any) => <option key={u.admn_unt_id} value={u.admn_unt_id}>{u.admn_unt_nm}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Ward</label>
                  <input type="text" value={ward_nm} onChange={(e) => setWardNm(e.target.value)} className={inputCls} placeholder="Ward name" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Location</label>
                  <input type="text" value={lctn_tx} onChange={(e) => setLctnTx(e.target.value)} className={inputCls} placeholder="Location description" />
                </div>
                <div>
                  <label className={labelCls}>Estimated Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                    <input type="number" min="0" value={orgnl_estmt_at || ''} onChange={(e) => setOrgnlEstmtAt(parseFloat(e.target.value) || 0)}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="0" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Priority No</label>
                  <input type="number" min="1" value={prty_nu} onChange={(e) => setPrtyNu(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className={inputCls} placeholder="e.g. 1" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Remarks</label>
                  <textarea value={rmrks_tx} onChange={(e) => setRmrksTx(e.target.value)} rows={2} className={inputCls} placeholder="Remarks" />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t flex items-center justify-end gap-2 bg-white rounded-b-xl">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving || !wrk_nm.trim()}
              className="px-5 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Creating...</> : <><i className="ri-check-line"></i> Create Work</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
